import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormikContext } from "formik";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  useTheme,
} from "@mui/material";
import { Warning, ArrowUpRight } from "@phosphor-icons/react";
import { getAllPhysicians, getUserPermissionsList } from "selectors";
import { providerTypes } from "./providerTypes";
import { formatToPhoneNumber } from "utils/formatFormFields";
import actions from "actions";
import ProviderClinicSection from "./ProviderClinicSection";

const NEW_PROVIDER_VALUE = "new";

interface ProvidersFormDialogProvidersFormProps {
  submitAttempted: boolean;
}

const ProvidersFormDialogProvidersForm: React.FC<ProvidersFormDialogProvidersFormProps> = ({
  submitAttempted,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const allPhysicians: any[] = useSelector(getAllPhysicians);
  const userPermissions: any = useSelector(getUserPermissionsList);
  const canManageLibraries = Boolean(userPermissions?.view_libraries);
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<any>();
  const isEdit = values.isEdit;
  const isNewProvider = values.provider?.id === NEW_PROVIDER_VALUE;
  const showReferringCheckbox =
    values.provider?.type === "primary care" || values.provider?.type === "diagnosing";

  useEffect(() => {
    dispatch(actions.getAllPhysicians());
  }, [dispatch]);

  const showProviderTypeError =
    (submitAttempted || (touched.provider as any)?.type) && (errors.provider as any)?.type;
  const showProviderIdError =
    (submitAttempted || (touched.provider as any)?.id) && (errors.provider as any)?.id;

  return (
    <>
      <Box style={{ marginBottom: "24px" }}>
        <FormControl fullWidth error={!!showProviderTypeError}>
          <InputLabel>Provider type</InputLabel>
          <Select
            name="provider.type"
            label="Provider type"
            value={values.provider?.type || ""}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            {providerTypes.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
          {showProviderTypeError && (
            <FormHelperText>{(errors.provider as any)?.type}</FormHelperText>
          )}
        </FormControl>
      </Box>
      <Box style={{ marginBottom: "24px" }}>
        <FormControl fullWidth error={!!showProviderIdError}>
          <InputLabel>Provider</InputLabel>
          <Select
            name="provider.id"
            label="Provider"
            value={values.provider?.id || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isEdit}
          >
            <MenuItem value={NEW_PROVIDER_VALUE}>Add new provider</MenuItem>
            <Divider />
            {allPhysicians.map(({ id, first_name, last_name, has_clinic }: any) => {
              const hasNoClinic = !has_clinic;
              if (!hasNoClinic) {
                return (
                  <MenuItem key={id} value={id}>
                    {`${first_name} ${last_name}`}
                  </MenuItem>
                );
              }
              return (
                <MenuItem
                  key={id}
                  value={id}
                  disabled
                  sx={{ "&.Mui-disabled": { opacity: 1, pointerEvents: "auto" } }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <span style={{ opacity: 0.38 }}>{`${first_name} ${last_name}`}</span>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ pointerEvents: "auto" }}>
                      <Tooltip title="Add a clinic to this provider's record to include them on the care team.">
                        <Warning
                          size={16}
                          weight="duotone"
                          color={theme.palette.warning.main}
                          data-testid={`${id}-info-icon`}
                        />
                      </Tooltip>
                      {canManageLibraries && (
                        <Tooltip title="Go to Provider Library">
                          <IconButton
                            aria-label="Go to Provider Library"
                            size="small"
                            component="a"
                            href={`/libraries#providers?id_provider=${id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ArrowUpRight size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
          {showProviderIdError && <FormHelperText>{(errors.provider as any)?.id}</FormHelperText>}
        </FormControl>
      </Box>
      {isNewProvider && (
        <>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.firstName"
              label="First name"
              value={values.provider?.firstName || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={submitAttempted && !!(errors.provider as any)?.firstName}
              helperText={submitAttempted && (errors.provider as any)?.firstName}
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.lastName"
              label="Last name"
              value={values.provider?.lastName || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={submitAttempted && !!(errors.provider as any)?.lastName}
              helperText={submitAttempted && (errors.provider as any)?.lastName}
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.medicaidId"
              label="Medicaid ID (optional)"
              value={values.provider?.medicaidId || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.npiNumber"
              label="NPI number (optional)"
              value={values.provider?.npiNumber || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.specialty"
              label="Specialty (optional)"
              value={values.provider?.specialty || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="provider.acceptsInsurance"
                  checked={values.provider?.acceptsInsurance || false}
                  onChange={(e) => setFieldValue("provider.acceptsInsurance", e.target.checked)}
                />
              }
              label="Accepts insurance"
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.email"
              label="Email address"
              value={values.provider?.email || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={submitAttempted && !!(errors.provider as any)?.email}
              helperText={submitAttempted && (errors.provider as any)?.email}
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.phone"
              label="Provider's phone number"
              value={formatToPhoneNumber(values.provider?.phone || "")}
              onChange={(e) =>
                setFieldValue(
                  "provider.phone",
                  e.target.value.replace(/[^\d]/g, "").slice(0, 10)
                )
              }
              onBlur={handleBlur}
              error={submitAttempted && !!(errors.provider as any)?.phone}
              helperText={submitAttempted && (errors.provider as any)?.phone}
              inputProps={{ maxLength: 14 }}
            />
          </Box>
          <Box style={{ marginBottom: "24px" }}>
            <TextField
              fullWidth
              name="provider.faxNumber"
              label="Provider's fax number (optional)"
              value={formatToPhoneNumber(values.provider?.faxNumber || "")}
              onChange={(e) =>
                setFieldValue(
                  "provider.faxNumber",
                  e.target.value.replace(/[^\d]/g, "").slice(0, 10)
                )
              }
              onBlur={handleBlur}
              inputProps={{ maxLength: 14 }}
            />
          </Box>
          <ProviderClinicSection submitAttempted={submitAttempted} />
        </>
      )}
      {showReferringCheckbox && (
        <Box style={{ marginBottom: "24px" }}>
          <FormControlLabel
            control={
              <Checkbox
                name="provider.isReferringProvider"
                checked={values.provider?.isReferringProvider || false}
                onChange={(e) => setFieldValue("provider.isReferringProvider", e.target.checked)}
              />
            }
            label="Mark this provider as the referring provider"
          />
        </Box>
      )}
    </>
  );
};

export default ProvidersFormDialogProvidersForm;
